import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import {
  SplitView,
  SplitViewMain,
  SplitViewSide,
} from 'components/SplitView/SplitView'
import { SplitViewSidebarListItem } from 'components/SplitView/SplitViewSidebarListItem'
import ScrollView from 'components/ScrollView'
import Spinner from 'components/Spinner'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import { CallCenterSearchResult } from 'call-center/models/CallCenterSearchResult'
import { InviteToWorkspaceOptions } from '../types'
import ClientDetails from '../ClientDetails'
import styles from './CustomerLookupSection.sass'
import sharedStyles from '../CallCenter.sass'
import { CallCenterCustomerLookupViewModel } from 'call-center/CallCenterCustomerLookupViewModel'
import { CallCenterMembershipItem } from 'call-center/CallCenterSelectionViewModel'
import { Form } from 'components/inputs'

interface CustomerLookupSectionProps {
  vm: CallCenterCustomerLookupViewModel
  onAddClient: () => void
  onInviteToWorkspace: (options: InviteToWorkspaceOptions) => void
  onCreateJob: (membership: CallCenterMembershipItem) => void
}

const CustomerLookupSection = observer(function CustomerLookupSection({
  vm,
  onAddClient,
  onInviteToWorkspace,
  onCreateJob,
}: CustomerLookupSectionProps) {
  return (
    <div className={styles.lookup}>
      <Form
        className={styles.lookupHeader}
        onSubmit={() => vm.searchText.change(vm.searchText.value, true)}
      >
        <input
          className={styles.searchInput}
          value={vm.searchText.value}
          onChange={(event) => vm.searchText.change(event.target.value)}
          placeholder="Search by name, email, or phone"
        />
        {vm.showAddClientButton && (
          <Button type="primary" onClick={onAddClient}>
            Add client
          </Button>
        )}
      </Form>

      <SplitView flexChild>
        <SplitViewSide narrow>
          <div className={styles.sidebarContent}>
            {vm.isSearching && (
              <div className={sharedStyles.spinnerContainer}>
                <Spinner size={20} />
              </div>
            )}
            {vm.shouldShowEmptyState && (
              <div className={styles.emptyState}>
                {vm.lastQuery
                  ? 'No matching clients were found. Try a different search or add a new client.'
                  : 'Start by searching for a client.'}
              </div>
            )}
            {(vm.results.length > 0 || vm.shouldShowResultsLoader) && (
              <ScrollView>
                <div className={styles.resultsList}>
                  {vm.results.map((result) => (
                    <ResultListItem
                      key={result.key}
                      result={result}
                      active={
                        vm.callCenterSelectionVM.selectedResult?.key ===
                        result.key
                      }
                      onSelect={() => vm.selectResult(result)}
                    />
                  ))}
                  {vm.shouldShowResultsLoader && (
                    <InfiniteScrollLoader
                      scrollProvider={vm.scroller}
                      compact
                    />
                  )}
                </div>
              </ScrollView>
            )}
          </div>
        </SplitViewSide>

        <SplitViewMain>
          {vm.callCenterSelectionVM.hasSelection ? (
            <ClientDetails
              vm={vm}
              onInviteToWorkspace={onInviteToWorkspace}
              onCreateJob={onCreateJob}
            />
          ) : (
            <div className={styles.placeholder}>
              Select a contact to view their details
            </div>
          )}
        </SplitViewMain>
      </SplitView>
    </div>
  )
})

interface ResultListItemProps {
  result: CallCenterSearchResult
  active: boolean
  onSelect: () => void
}

const ResultListItem = observer(function ResultListItem({
  result,
  active,
  onSelect,
}: ResultListItemProps) {
  const { hubspotContact: contact, workspaceMembers } = result
  const displayName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(' ')
    : workspaceMembers[0]?.member.displayName || 'Unknown client'
  const email = contact?.email || workspaceMembers[0]?.member.email || ''
  const phoneNumber = contact?.phone || workspaceMembers[0]?.member.phone || ''

  return (
    <SplitViewSidebarListItem
      active={active}
      onClick={onSelect}
      spacing="compact"
    >
      <div className={styles.resultContainer}>
        <div className={styles.resultName}>
          {displayName || email || 'Unknown'}
        </div>
        {email && <div className={styles.resultContactInfo}>{email}</div>}
        {phoneNumber && (
          <div className={styles.resultContactInfo}>{phoneNumber}</div>
        )}
      </div>
    </SplitViewSidebarListItem>
  )
})

export default CustomerLookupSection
